import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { formatDistanceToNow, type Locale } from 'date-fns';
import { fr, enUS, de } from 'date-fns/locale';
import type { AppNotification } from '../../services/notifications.service';

/** Type → icon. Unknown types fall back to a generic bell. */
const ICON_BY_TYPE: Record<string, ReactElement> = {
  MESSAGE: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
  PAYMENT: <PaymentsRoundedIcon fontSize="small" />,
  CONTRIBUTION: <VolunteerActivismRoundedIcon fontSize="small" />,
  MEETING: <EventRoundedIcon fontSize="small" />,
  REMINDER: <AlarmRoundedIcon fontSize="small" />,
  SECURITY: <ShieldRoundedIcon fontSize="small" />,
};

const DATE_LOCALES: Record<string, Locale> = { fr, en: enUS, de };

/**
 * One notification row — shared by the header bell dropdown and the full
 * notifications page. Unread rows are tinted and carry a dot.
 */
export function NotificationItem({
  notification,
  onClick,
}: {
  notification: AppNotification;
  onClick: (n: AppNotification) => void;
}) {
  const { i18n } = useTranslation();
  const icon = ICON_BY_TYPE[notification.type] ?? <NotificationsRoundedIcon fontSize="small" />;
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: DATE_LOCALES[i18n.language] ?? fr,
  });

  return (
    <ListItemButton
      onClick={() => onClick(notification)}
      sx={{
        alignItems: 'flex-start',
        gap: 0.5,
        bgcolor: notification.read ? 'transparent' : 'action.hover',
      }}
    >
      <ListItemIcon sx={{ minWidth: 38, mt: 0.5, color: 'primary.main' }}>{icon}</ListItemIcon>
      <ListItemText
        primary={notification.title}
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: notification.read ? 500 : 700,
        }}
        secondary={
          <>
            <Typography
              variant="body2"
              component="span"
              color="text.primary"
              sx={{ display: 'block' }}
            >
              {notification.body}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeAgo}
            </Typography>
          </>
        }
      />
      {!notification.read && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            mt: 1,
            flexShrink: 0,
          }}
        />
      )}
    </ListItemButton>
  );
}
